import {Inject, Injectable} from "@angular/core";
import {RegistrationService} from "../registration/registration.service";
import {AccountService} from "../account/account.service";
import {FormBuilder, Validators} from "@angular/forms";
import {OrderService} from "../order/order.service";
import {ExchangeService} from "../exchange/exchange.service";
import {ProductService} from "../product/product.service";
import {Subject} from "rxjs";
import {CountryService} from "../country/country.service";
import {CustomValidators} from "../validators/custom.validators";
import {AccountControllerService} from "@aparraga/ahoracrypto-api-sdk/sdk/api/accountController.service";
import {CartService} from "../cart/cart.service";
import {CurrencyService} from "../currency/currency.service";
import {CryptoService} from "../crypto/crypto.service";
import {AbstractFlowHelperService} from "./abstractflowhelper.service";
import {I18nServices} from "../i18n/i18n.service";
import { NavigationService } from "../navigation/navigation.service";

@Injectable({
  providedIn: 'root'
})
export class PurchaseFlowHelperService extends AbstractFlowHelperService {

  public selectedCurrency;
  public selectedCurrencyName;
  public selectedCrypto;
  public selectedCryptoName;
  public selectedCryptoPrecision;
  public selectedNetwork;
  public selectedNetworkName;
  public selectedNetworkMemoRegex;

  public onCurrencySelected$ = new Subject<any>();
  public onCryptoSelected$ = new Subject<any>();
  public onNetworkSelected$ = new Subject<any>();

  //set and get of currenciespopup
  set currenciespopup(value: any) {
    this._currenciespopup = value;
    this._currenciespopup.onCurrencySelected.subscribe(currency => {
      this.onCurrencySelected$.next(currency);
      this.selectedCurrency = currency;
      this.selectedCurrencyName = this.currencyService.getCurrency(currency).name;
      this.updateControlAmounts();
    });
  }

  get currenciespopup(): any {
    return this._currenciespopup;
  }

  set cryptospopup(value: any) {
    this._cryptospopup = value;
    this._cryptospopup.onCryptoSelected.subscribe(crypto => {
      this.onCryptoSelected$.next(crypto);
      this.selectedCrypto = crypto;
      this.updateControlAmounts();
    });
  }

  get cryptospopup(): any {
    return this._cryptospopup;
  }

  protected _currenciespopup: any;
  protected _cryptospopup: any;
  protected _networkspopup: any;

  public lastTouchedControl: any;

  protected stopExchangeConversion: boolean = false;

  public constructor(protected registrationService: RegistrationService,
                     protected accountService: AccountService,
                     @Inject(FormBuilder) protected formBuilder: FormBuilder,
                     protected exchangeService: ExchangeService,
                     protected customValidators: CustomValidators,
                     protected countryService: CountryService,
                     protected navigationService: NavigationService,
                     protected i18nServices: I18nServices,
                     protected currencyService: CurrencyService,
                     protected cryptoService: CryptoService,
                     protected accountControllerService: AccountControllerService,
                     protected productService: ProductService,
                     protected cartService: CartService,
                     protected orderService: OrderService) {

    super();

    this.buildForm();

    this.exchangeService.exchangePricesSubject$.subscribe(exchangeRates => {
      this.updateControlAmounts();
    });

    this.navigationService.postMessages$.subscribe(message => {
      if (message && message.type === 'SET_WALLET_ADDRESS') {
        this.form.get('destinationWalletAddress').setValue(message['walletAddress']);
        // set the network to the default one for the crypto
        const defaultNetwork = this.cryptoService.getDefaultNetworkForCurrentCrypto();
        if(defaultNetwork != null) {
          this.form.get('destinationWalletNetwork').setValue(defaultNetwork.network);
        }
      }
    });

    let selectedCurrency = this.currencyService.getSelectedCurrency();
    if(selectedCurrency != null) {
      this.selectedCurrency = selectedCurrency.code;
      this.selectedCurrencyName = selectedCurrency.name;
    }

    this.currencyService.currencySubject$.subscribe(currency => {
      this.selectedCurrency = currency.code;
      this.selectedCurrencyName = currency.name;
    });

    this.cryptoService.cryptoSubject$.subscribe(crypto => {
      if(crypto != null) {
        this.selectedCrypto = crypto.baseCurrency;
        this.productService.getProduct(this.selectedCrypto).then(product => {
          this.selectedCryptoName = product.name;
          this.selectedCryptoPrecision = product.precision;
          this.updateControlAmounts();
        });
      }
    });

    this.cryptoService.networkSubject$.subscribe(network => {
      if(network != null) {
        this.selectedNetwork = network.network;
        this.selectedNetworkName = network.name;
        this.selectedNetworkMemoRegex = network.memoRegex;
      }
    });

  }

  public buildForm() {

    let items = this.orderService.getOrder().orderLines;
    let currencyAmount = null;
    let destinationWalletAddress = null;
    let destinationWalletNetwork = null;
    let destinationWalletMemo = null;
    let acceptRisksEnteringManualWallet = false;
    if(items.length == 1) {
      let item = items[0];
      currencyAmount = item.totalToPayFor;
      destinationWalletAddress = item.walletAddress;
      destinationWalletNetwork = item.walletNetwork;
      destinationWalletMemo = item.walletMemo;
    }
    else {
      currencyAmount = 100; //get this value by default to prepopulate the form
    }

    this.form = this.formBuilder.group({
      currencyAmount: [isNaN(currencyAmount) || +currencyAmount == 0?null:currencyAmount.toString(), [
        Validators.required,
        this.customValidators.validateBaseCurrencyAmount()
      ]],
      cryptoAmount: [null, [
        Validators.required,
        //todo validate amount
      ]],
      destinationWalletAddress: [destinationWalletAddress, [
        Validators.required,
      ], this.customValidators.validateWalletAddress()],
      destinationWalletNetwork: [destinationWalletNetwork, [
        Validators.required
      ]],
      destinationWalletMemo: [destinationWalletMemo, [
      ], this.customValidators.validateWalletMemo()],
      acceptRisksEnteringManualWallet: [ acceptRisksEnteringManualWallet, [
        Validators.requiredTrue
      ]]
    });

    if(currencyAmount != null) {
      //wait all async calls in the constructor to be done
      this.lastTouchedControl = 'currencyAmount';
    }

    this.form.valueChanges.subscribe(val => {
      this.updateData();
    });

    this.form.get('currencyAmount').valueChanges.subscribe(value => {
      //check that value is a number
      if(!this.stopExchangeConversion) {
        this.stopExchangeConversion = true;
        this.updateControlAmounts();
        this.stopExchangeConversion = false;
      }
    });

    this.form.get('cryptoAmount').valueChanges
      .subscribe(value => {
        if(!this.stopExchangeConversion) {
          this.stopExchangeConversion = true;
          this.updateControlAmounts();
          this.stopExchangeConversion = false;
        }
      });

    this.form.get('destinationWalletNetwork').valueChanges
      .subscribe(value => {
        //validate address on network change
        if(this.form.get('destinationWalletAddress').value != null && this.form.get('destinationWalletAddress').value != '') {
          this.form.get('destinationWalletNetwork').markAsTouched();
          this.form.get('destinationWalletMemo').markAsTouched();
          this.form.get('destinationWalletAddress').updateValueAndValidity();
        }
      });

    let selectedCrypto = this.cryptoService.getSelectedCrypto();
    if(selectedCrypto != null) {
      this.selectedCrypto = selectedCrypto.baseCurrency;
      this.productService.getProduct(selectedCrypto.baseCurrency).then(product => {
        this.selectedCryptoName = product.name;
        this.selectedCryptoPrecision = product.precision;
        this.updateControlAmounts();
      });
    }

  }

  public updateCart() {
    let promise: Promise<boolean> = new Promise(async (resolve, reject) => {
      this.productService.getProduct(this.selectedCrypto).then(product => {
          let exchangePrice = this.exchangeService.getExchangePrice(this.selectedCrypto, this.selectedCurrency);
          let totalToPayFor = this.i18nServices.parseNumber(this.form.get('currencyAmount').value);
          let totalToReceiveFrom = this.i18nServices.parseNumber(this.form.get('cryptoAmount').value);
          let cryptoQuantityCanVary = true;
          if(this.lastTouchedControl == 'cryptoAmount') {
            cryptoQuantityCanVary = false;
          }
          let walletAddress = this.form.get('destinationWalletAddress').value;
          let walletNetwork = this.form.get('destinationWalletNetwork').value;
          let walletMemo = this.form.get('destinationWalletMemo').value;
          this.cartService.clearAndAddItem(product, exchangePrice, totalToReceiveFrom, totalToPayFor, cryptoQuantityCanVary, walletAddress, walletNetwork, walletMemo);
          resolve(true);
        },
        error => {
          reject(false);
        });
    });

    return promise;
  }

  protected updateControlAmounts() {
    if(this.lastTouchedControl == 'currencyAmount') {
      let value = this.i18nServices.parseNumber(this.form.get('currencyAmount').value);
      if (value && !isNaN(value) && this.selectedCrypto != null) {
        let receive = this.exchangeService.calculateExchange(this.selectedCurrency, value, this.selectedCrypto);
        let precision = this.selectedCryptoPrecision;
        if(precision != null) {
          this.form.get('cryptoAmount').setValue(this.i18nServices.formatNumber(receive, precision, null, false, false));
        }
      }
      else {
        this.form.get('cryptoAmount').setValue("");
      }
    }
    else if(this.lastTouchedControl == 'cryptoAmount') {
      let value = this.i18nServices.parseNumber(this.form.get('cryptoAmount').value);
      if (value && !isNaN(value)) {
        let spend = this.exchangeService.calculateExchange(this.selectedCrypto, value, this.selectedCurrency);
        this.form.get('currencyAmount').setValue(this.i18nServices.formatNumber(spend, 0, 2, false, false));
      }
      else {
        this.form.get('currencyAmount').setValue("");
      }
    }
  }

  public selectCurrency(currency) {
    this.selectedCurrency = currency;
    this.selectedCurrencyName = this.currencyService.getCurrency(currency).name;
    this.updateControlAmounts();
  }

  public selectCrypto(symbol) {
    let crypto = this.exchangeService.getExchangePrice(symbol, this.selectedCurrency);
    this.selectedCrypto = symbol;
    this.selectedCryptoName = this.cryptoService.getCryptocurrencyName(crypto.baseCurrency);
    this.productService.getProduct(this.selectedCrypto).then(product => {
      this.selectedCryptoPrecision = product.precision;
      this.updateControlAmounts();
    });
  }

  protected updateData() {
    for(let input in this.form.value) {
      //todo ???
    }
  }

}
